/**
 * @see {@link https://www.abeautifulsite.net/posts/opening-a-new-window-after-an-async-operation/}
 */
export function newWindow(fn: () => Promise<string>) {
  const window = open()!;
  (async () => {
    try {
      window.location.href = await fn();
    } catch {
      window.close();
    }
  })();
}

/**
 * Handle returned by openDeferredWindow. The caller resolves it with the final
 * URL once an async operation (e.g. a server call) completes, OR cancels it if
 * the redirect is no longer needed. The handle takes care of the popup-blocker
 * fallback: if the speculative window was blocked, resolve() falls back to
 * navigating the current tab so the user isn't stranded.
 */
export interface DeferredWindow {
  /** Send the resolved URL to the pre-opened tab, or fall back to a same-tab
   *  redirect if the popup was blocked or has been closed. */
  resolve(url: string): void;
  /** Close the pre-opened tab without redirecting (e.g. the resolved
   *  destination turned out to be in-app, or no redirect is needed). */
  cancel(): void;
}

/**
 * Open a tab synchronously inside a click handler so the navigation survives
 * Chrome/Firefox/Safari popup blockers, then set its location once an awaited
 * URL is known. Must be called from a user-gesture handler — calling
 * `window.open` after an `await` boundary fails the activation check and the
 * popup is silently blocked.
 *
 * Usage:
 *   onClick={() => {
 *     const tab = openDeferredWindow();
 *     void doAsyncWork().then(
 *       (url) => tab.resolve(url),
 *       () => tab.cancel(),
 *     );
 *   }}
 *
 * Behavior:
 *  - If the browser blocked the popup, `open()` returns null and `resolve()`
 *    falls back to `location.href = url` so the user still reaches the
 *    destination (in the same tab).
 *  - If the user closed the speculative tab before resolution, the fallback
 *    also runs.
 *  - `cancel()` closes the speculative tab if it's still open.
 *
 * `noopener,noreferrer` is intentionally NOT passed: the Window reference is
 * required to set `location.href` later. The tab starts at `about:blank`, so
 * there's no opener leak before the resolved navigation.
 */
export function openDeferredWindow(): DeferredWindow {
  const speculative = window.open("about:blank", "_blank");
  let settled = false;
  return {
    resolve(url: string) {
      if (settled) {
        return;
      }
      settled = true;
      if (speculative && !speculative.closed) {
        speculative.location.href = url;
      } else {
        location.href = url;
      }
    },
    cancel() {
      if (settled) {
        return;
      }
      settled = true;
      if (speculative && !speculative.closed) {
        speculative.close();
      }
    },
  };
}
